import {
  DropdownOpener,
  FloatElement,
} from 'src/scripts/components/float-element'
import { BaseComponent } from '../../components/base-component'
import { Nullable } from '../../types/common'
import { isMobile } from '../../utils/check-media'
import { debounce } from '../../utils/debounce'
import { setStyleVariable } from '../../utils/dom'
import { BodyElement } from 'src/scripts/components/body-element/body-element'
import { $el, $list, transitionToPromise } from 'src/scripts/utils/utils'

const HEADER_NAV_SELECTOR = '[data-desktop-nav]'
const BURGER_ICON_SELECTOR = '.drawer-menu-toggle'
const HEADER_GRID_SELECTOR = '.header__grid'
const HEADER_DROPDOWN_BACKDROP_SELECTOR = '[data-header-dropdown-backdrop]'
const HEADER_DROPDOWN_WITH_BACKDROP_BTN_SELECTOR =
  'data-header-btn-with-backdrop'
const SECTION_SELECTOR = '.shopify-section-header'
const BODY_ELEMENT_SELECTOR = 'body-element'

export class HeaderComponent extends BaseComponent {
  headerGrid: HTMLElement
  navContainer: HTMLElement
  burgerIcon: HTMLElement
  navFirstItem: HTMLElement
  navLastItem: HTMLElement
  dropdownsBackdrop: HTMLElement
  headerLinks: (HTMLElement | FloatElement)[]
  dropdowns: FloatElement[]
  openers: DropdownOpener[]
  openedElementId: boolean

  lastScrollPosition: number = 0
  headerSectionBottom: number
  resizeObserver: ResizeObserver
  isHidden: boolean
  lastBottom: number
  lastHeight: number
  navWidth: number

  isSticky: boolean
  isCurrentSticky: boolean
  isDynamicSticky: boolean
  stickyVariant: string
  isTransparent: boolean
  blockInEditorSelected: boolean

  mountComponent(): void {
    this.isSticky = this.hasAttribute('is-sticky')
    this.isTransparent = this.hasAttribute('is-transparent')

    if (this.isTransparent) {
      this.setTransparentHeader()
    }

    this.updatePosition()

    this.addListener(window, 'scroll', this.handleScroll)

    this.moveDrawer()

    this.navContainer = $el(HEADER_NAV_SELECTOR, this)

    if (this.navContainer) {
      this.setNavItems()
      this.checkIsNavContainerFitAllLinks()

      this.dropdownsBackdrop = $el(HEADER_DROPDOWN_BACKDROP_SELECTOR, this)
      const isWithOpenOnClick = this.hasAttribute('with-open-on-click')

      if (!isWithOpenOnClick) {
        const btns = $list('[data-header-btn-with-backdrop]')

        customElements.whenDefined('header-float-element-btn').then(() => {
          btns.forEach((btn: DropdownOpener) => {
            this.addListener(btn, 'mouseenter', this.handleBtnMouseEnter)
          })
        })
      } else {
        this.openers = $list('dropdown-opener[data-header-btn-with-backdrop]')

        customElements.whenDefined('dropdown-opener').then(() => {
          this.openers.forEach((opener: DropdownOpener) => {
            opener.on('openerClick', this.handleOpenerClick)
            opener.on('openerClickOutside', this.handleOpenerClickOutside)
          })
        })
      }
    }

    this.resizeObserver = new ResizeObserver(this.handleResize)
    this.resizeObserver.observe(this)

    if (this.isEditor) {
      this.blockInEditorSelected = false
    }
  }

  unmountComponent(): void {
    this.resizeObserver.disconnect()
  }

  handleBtnMouseEnter = ({ currentTarget }) => {
    this.handleButtonHover(currentTarget)

    this.addListener(document, 'mousemove', this.handleMouseMove)
  }

  handleOpenerClick = ({ element, opener }) => {
    this.openers.forEach(el => {
      el.setExpand(el === opener)
    })

    if (this.openedElementId === element.floatElementId) {
      this.handleMouseLeave()
      opener.setExpand(false)
    } else {
      this.handleButtonHover(element)
    }
  }

  handleOpenerClickOutside = ({ element, opener }) => {
    this.handleMouseLeave()
    opener.setExpand(false)
  }

  handleMouseMove = debounce(({ target }) => {
    const isButton = target.closest(
      `[${HEADER_DROPDOWN_WITH_BACKDROP_BTN_SELECTOR}]`,
    )

    const isInside =
      target.closest('.header__dropdown-menu') || target.closest('#Portal')

    if (!isButton && !isInside) {
      this.handleMouseLeave()
    }
  }, 20)

  handleButtonHover = button => {
    this.emit('headerButtonHover', {
      floatElementId: button.floatElementId,
    })

    const height = this.getFloatElementHeight(button.floatElement)
    this.expandDropdownBackdrop(height)

    this.openedElementId = button.floatElementId
  }

  getFloatElementHeight = floatElement => {
    const height =
      floatElement.maxHeight || floatElement.currentVisible.offsetHeight

    return height
  }

  handleMouseLeave = () => {
    this.openedElementId = null

    if (this.isEditor && this.blockInEditorSelected) {
      return
    }

    this.collapseDropdownBackdrop()
    this.emit('mouseLeavedHeader', {})

    this.removeListener(document, 'mousemove', this.handleMouseMove)
  }

  expandDropdownBackdrop = height => {
    this.dropdownsBackdrop.setAttribute(
      'style',
      `--gsc-header-backdrop-height: ${height}px;`,
    )

    transitionToPromise(this.dropdownsBackdrop).then(() => {
      this.dropdownsBackdrop.setAttribute('with-opened-dropdown', '')
    })
  }

  collapseDropdownBackdrop = () => {
    this.dropdownsBackdrop.setAttribute(
      'style',
      `--gsc-header-backdrop-height: 0px;`,
    )

    this.dropdownsBackdrop.removeAttribute('with-opened-dropdown')

    this.removeListener(this.navContainer, 'mousemove', this.handleMouseMove)
  }

  setNavItems = () => {
    this.headerGrid = $el(HEADER_GRID_SELECTOR, this)

    const navItems = $list('.header__item', this.navContainer)

    this.navFirstItem = navItems[0] as HTMLElement
    this.navLastItem = navItems[navItems.length - 1] as HTMLElement

    this.burgerIcon = $el(BURGER_ICON_SELECTOR, this).cloneNode(
      true,
    ) as HTMLElement
    this.burgerIcon.setAttribute('mock', '')

    const navFirstItemLeft = this.navFirstItem.getBoundingClientRect().left
    const navLastItemRight = this.navLastItem.getBoundingClientRect().right
    this.navWidth = navLastItemRight - navFirstItemLeft + 40
  }

  checkIsNavContainerFitAllLinks = () => {
    if (!this.navContainer) {
      return
    }

    const navFirstItemLeft = this.navFirstItem.getBoundingClientRect().left
    const navLastItemRight = this.navLastItem.getBoundingClientRect().right

    let prevElement = this.navContainer.previousElementSibling as Element
    let nextElement = this.navContainer.nextElementSibling as Element

    let prevElementRight = prevElement?.getBoundingClientRect().right
    let nextElementleft = nextElement?.getBoundingClientRect().left

    if (this.headerGrid.className.includes('header__grid--3')) {
      prevElementRight = navFirstItemLeft
      nextElementleft = prevElement?.getBoundingClientRect().left
    }

    if (
      this.headerGrid.className.includes('header__grid--4') ||
      this.headerGrid.className.includes('header__grid--5')
    ) {
      prevElementRight = navFirstItemLeft
      nextElementleft = this.getBoundingClientRect().right - 50
    }

    const burgerIconWidth = this.burgerIcon.getBoundingClientRect().width

    const currWidthForNav = nextElementleft - prevElementRight + burgerIconWidth

    const isToAddBurger = currWidthForNav <= this.navWidth

    this.navContainer.toggleAttribute('hidden', isToAddBurger)
    this.headerGrid.toggleAttribute('fit-nav', isToAddBurger)

    if (isToAddBurger) {
      this.headerGrid.prepend(this.burgerIcon)
      this.emit('drawerActivated', {})
    } else {
      this.burgerIcon.firstElementChild.classList.remove('is-opened')
      this.burgerIcon.remove()
      this.emit('drawerUnActivated', {})
    }
  }

  moveDrawer = () => {
    const drawer = $el('drawer-menu', this.section)

    if (!drawer) {
      return
    }

    if (this.isEditor) {
      const nextSibling = this.section.nextElementSibling

      if (nextSibling.tagName === 'DRAWER-MENU') {
        nextSibling.remove()
      }
    }

    this.section.after(drawer)
  }

  handleResize = debounce((): void => {
    if (this.isTransparent) {
      this.setTransparentHeader()
    }

    this.setHidden(false)
    this.updatePosition()

    const rect = this.section.getBoundingClientRect()

    this.headerSectionBottom = rect.top

    if (!isMobile() && this.navContainer) {
      this.checkIsNavContainerFitAllLinks()
    }

    this.isCurrentSticky = this.isSticky || (!this.isSticky && isMobile())

    if (this.isCurrentSticky) {
      this.setSticky()
    } else {
      this.removeSticky()
    }

    this.section.setAttribute('visible', '')
  }, 100)

  handleScroll = debounce((): void => {
    if (this.isCurrentSticky) {
      this.updateVisibleAfterScroll()
    }

    this.setScrollStatus()
  }, 20)

  setScrollStatus(): void {
    if (!this.section) {
      return
    }

    const savedScrollPosition = Math.abs(
      parseInt(document.body.style.top || '0'),
    )
    const scrollPosition = window.scrollY || savedScrollPosition

    const scrolled = this.isDynamicSticky
      ? scrollPosition - 30 > this.lastBottom
      : scrollPosition > this.headerSectionBottom

    this.section.classList.toggle('scrolled', scrolled)
  }

  updateVisibleAfterScroll(): void {
    const bodyElement: BodyElement = $el(BODY_ELEMENT_SELECTOR)

    if (!this.section || bodyElement.isFixed) {
      return
    }

    const scrollY = window.scrollY
    const threshold = 36
    const scrollDistance = scrollY - this.lastScrollPosition
    const isScrollUp = scrollDistance < 0
    const isScrollDown = scrollDistance > 0
    const isUnderHeader = scrollY > this.headerSectionBottom

    const showOnScrollUp =
      isScrollUp &&
      this.isHidden &&
      scrollDistance + threshold < 0 &&
      isUnderHeader

    const hideOnScrollDown =
      isScrollDown && scrollDistance > threshold && isUnderHeader

    if (showOnScrollUp) {
      this.setHidden(false)
    } else if (hideOnScrollDown) {
      this.setHidden(true)
    }

    this.lastScrollPosition = scrollY
  }

  setHidden(isHidden: boolean): void {
    if (!this.section) {
      return
    }

    this.isHidden = isHidden
    this.section.setAttribute('aria-hidden', isHidden ? 'true' : 'false')
  }

  setSticky(): void {
    this.stickyVariant = this.getAttribute('is-sticky')
    document.body.setAttribute('header-desktop-sticky', '')

    if (this.stickyVariant == 'dynamic' || (!this.isSticky && isMobile())) {
      this.section.setAttribute('dynamic-sticky', '')
      this.isDynamicSticky = true
    }

    if (
      !this.isSticky &&
      isMobile() &&
      !this.hasAttribute('with-sticky-colored')
    ) {
      this.section.setAttribute('with-sticky-colored', '')
    }
  }

  removeSticky(): void {
    this.section.removeAttribute('dynamic-sticky')
    document.body.removeAttribute('header-desktop-sticky')
    this.isDynamicSticky = false

    if (!this.hasAttribute('with-sticky-colored')) {
      this.section.removeAttribute('with-sticky-colored')
    }
  }

  updatePosition(): void {
    if (!this.section) {
      return
    }

    const rect = this.getBoundingClientRect()

    if (this.lastHeight !== rect.height) {
      setStyleVariable('header-height', `${rect.height}px`)
      this.lastHeight = rect.height
    }

    if (this.lastBottom !== rect.bottom) {
      setStyleVariable('header-bottom-side', `${rect.bottom}px`)
      this.lastBottom = rect.bottom
    }
  }

  setTransparentHeader = () => {
    const { height: headerHeight } = this.getBoundingClientRect()

    this.section.style.marginBottom = `-${headerHeight}px`

    this.section.setAttribute('transparent-setted', '')

    if (this.hasAttribute('with-sticky-colored')) {
      this.section.setAttribute('with-sticky-colored', '')
    }
  }

  get section(): Nullable<HTMLElement> {
    return $el(SECTION_SELECTOR)
  }
}
